import { Routes } from "~/routes";
import { NavGroup, NavItem } from "./types";
import { BadgeVariants } from "../Badge";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { IconTypes } from "../Icon/types";
import { ASSET_CLASS_PAGES_TO_ICON } from "~/modules/asset_classes/metadata";
import { RWA_URLS } from "~/consts";

const NEW_BADGE = {
    variant: BadgeVariants.blue,
    text: "NEW",
};

const BETA_BADGE = {
    variant: BadgeVariants.yellow,
    text: "BETA",
};

// // // //
// Nav Items

const RESEARCH_NAV_ITEM: NavItem = {
    key: "research",
    label: "Research Blog",
    href: Routes.blog,
    icon: IconTypes.FlaskGear,
};

const HOME_NAV_ITEM: NavItem = {
    key: "home",
    label: "Home",
    icon: IconTypes.Home,
    href: Routes.home,
};

const OVERVIEW_NAV_ITEM: NavItem = {
    key: "overview",
    label: "Overview",
    icon: IconTypes.Globe,
    href: Routes.overview,
};

const TRADE_NAV_ITEM: NavItem = {
    key: "trade",
    label: "Trade",
    icon: IconTypes.MoneyBillTransfer,
    href: Routes.trade,
    activeRoutes: [Routes.trade],
    featureFlag: FeatureNames.enableExperimentalFeatures,
    badge: BETA_BADGE,
};

const ASSET_CLASSES_NAV_ITEM: NavItem = {
    key: "asset_classes",
    label: "Asset Classes",
    icon: IconTypes.Shapes,
    defaultExpandedState: true,
    items: [
        {
            key: "stablecoins",
            label: "Stablecoins",
            icon: ASSET_CLASS_PAGES_TO_ICON.stablecoins,
            href: Routes.stablecoins,
        },
        {
            key: "treasuries",
            label: "U.S. Treasury Funds",
            href: Routes.treasuries,
            icon: ASSET_CLASS_PAGES_TO_ICON.treasuries,
        },
        {
            key: "government_bonds",
            label: "Non-U.S. Govt. Debt",
            href: Routes.governmentBonds,
            icon: ASSET_CLASS_PAGES_TO_ICON.government_bonds,
        },
        {
            key: "credit",
            label: "Credit",
            href: Routes.credit,
            icon: ASSET_CLASS_PAGES_TO_ICON.credit,
        },
        {
            key: "stocks",
            label: "Stocks",
            icon: ASSET_CLASS_PAGES_TO_ICON.stocks,
            href: Routes.stocks,
        },
        {
            key: "private_equity",
            label: "PE / VC",
            icon: ASSET_CLASS_PAGES_TO_ICON.pe_vc,
            href: Routes.privateEquity,
        },
        {
            key: "active_strategies",
            label: "Active Strategies",
            icon: ASSET_CLASS_PAGES_TO_ICON.active_strategies,
            href: Routes.activeStrategies,
        },
        {
            key: "commodities",
            label: "Commodities",
            icon: ASSET_CLASS_PAGES_TO_ICON.commodities,
            href: Routes.commodities,
        },
        {
            key: "real-estate",
            label: "Real Estate",
            href: Routes.realEstate,
            icon: ASSET_CLASS_PAGES_TO_ICON.real_estate,
        },
    ],
};

// // // //

const PROTOCOLS_NAV_ITEM: NavItem = {
    key: "platforms",
    label: "Platforms",
    href: Routes.platforms,
    icon: IconTypes.AtomSimple,
    activeRoutes: [Routes.platforms, Routes.platformIndividual],
};

const MANAGERS_NAV_ITEM: NavItem = {
    key: "managers",
    label: "Asset Managers",
    href: Routes.managers,
    icon: IconTypes.MoneyBills,
    activeRoutes: [Routes.managers, Routes.managerIndividual],
};

const NETWORKS_NAV_ITEM: NavItem = {
    key: "networks",
    label: "Networks",
    href: "/networks",
    icon: IconTypes.CircleNodes,
    activeRoutes: [Routes.networks, Routes.networkIndividual],
};

const SALES_PAGE: NavItem = {
    key: "platform-overview",
    label: "Enterprise API",
    href: Routes.salesPage,
    icon: IconTypes.CircleNodes,
    badge: NEW_BADGE,
    activeRoutes: [Routes.salesPage],
    featureFlag: FeatureNames.enableApiKeys,
    invertFeatureFlagBehavior: true,
};

const FLOWS_EXPLORER_NAV_ITEM: NavItem = {
    key: "flows-explorer",
    label: "Flows Explorer",
    href: Routes.flowsExplorer,
    icon: IconTypes.ArrowsRotate,
    badge: BETA_BADGE,
    featureFlag: FeatureNames.enableChartsPages,
    activeRoutes: [Routes.flowsExplorer],
};

const DATA_CATALOG_NAV_ITEM: NavItem = {
    key: "data-catalog",
    label: "Data Catalog",
    href: Routes.catalog,
    icon: IconTypes.Database,
    activeRoutes: [
        Routes.catalog,
        Routes.measures,
        Routes.measureIndividual,
        Routes.referenceData,
        Routes.referenceDataIndividual,
    ],
};

const CHARTS_NAV_ITEM: NavItem = {
    key: "charts",
    label: "Charts",
    href: Routes.charts,
    icon: IconTypes.Chart,
    badge: BETA_BADGE,
    featureFlag: FeatureNames.enableChartsPages,
    activeRoutes: [
        Routes.charts,
        Routes.chartIndividual,
        Routes.chartNew,
        Routes.chartEdit,
    ],
};

const DOCS_NAV_ITEM: NavItem = {
    key: "docs",
    label: "Documentation",
    href: RWA_URLS.docs,
    external: true,
    icon: IconTypes.Books,
};

const API_TOOLS_NAV_ITEM: NavItem = {
    key: "api_tools",
    label: "API Tools",
    icon: IconTypes.Code,
    href: Routes.apiToolsApiKeys,
    featureFlag: FeatureNames.enableApiKeys,
    activeRoutes: [
        Routes.apiTools,
        Routes.apiToolsApiKeys,
        Routes.apiToolsV4Assets,
        Routes.apiToolsV4Tokens,
        Routes.apiToolsV4TokenHolders,
        Routes.apiToolsV4TokenTimeseries,
        Routes.apiToolsV4AssetTimeseries,
        Routes.apiToolsV4Networks,
        Routes.apiToolsV4Platforms,
        Routes.apiToolsV4Protocols,
        Routes.apiToolsV4Managers,
        Routes.apiToolsV4Issuers,
        Routes.apiToolsV4ServiceProviders,
        Routes.apiToolsV4AssetClasses,
        Routes.apiToolsV4Transactions,
        Routes.apiToolsV4TokenAggregates,
        Routes.apiToolsV4AssetAggregates,
    ],
};

const MCP_TOOLS_NAV_ITEM: NavItem = {
    key: "mcp_tools",
    label: "MCP Server",
    href: Routes.toolsMcp,
    icon: IconTypes.Plug,
    badge: BETA_BADGE,
    featureFlag: FeatureNames.enableMcpTools,
    activeRoutes: [
        Routes.toolsMcp,
        Routes.oauthClients,
        Routes.oauthClientsNew,
        Routes.oauthClientDetail,
    ],
};

// const NEWS_NAV_ITEM: NavItem = {
//     key: "news",
//     label: "News",
//     href: Routes.news,
//     icon: IconTypes.Newspaper,
// };

const ASSET_SCREENER_NAV_ITEM: NavItem = {
    key: "asset_screener",
    label: "Asset Screener",
    href: Routes.assetScreener,
    icon: IconTypes.Search,
};

const NEW_ASSET_MONITOR_NAV_ITEM: NavItem = {
    key: "new_asset_monitor",
    label: "New Asset Monitor",
    href: Routes.newAssetMonitor,
    icon: IconTypes.Bell,
};

// const PRICING_NAV_ITEM: NavItem = {
//     key: "pro",
//     label: "Upgrade to Pro",
//     href: Routes.pricing,
//     icon: IconTypes.Lightning,
//     iconClassName: "text-link",
//     badge: NEW_BADGE,
//     featureFlag: FeatureNames.enableExperimentalFeatures,
// };

const MEDIA_CITATIONS_NAV_ITEM: NavItem = {
    key: "media_citations",
    label: "Press & Citations",
    href: Routes.citations,
    icon: IconTypes.Newspaper,
};

// // // //
// Groups

const HOME_GROUP: NavGroup = {
    key: "latest",
    label: "Latest",
    items: [
        HOME_NAV_ITEM,
        ASSET_SCREENER_NAV_ITEM,
        NEW_ASSET_MONITOR_NAV_ITEM,
        FLOWS_EXPLORER_NAV_ITEM,
        CHARTS_NAV_ITEM,
        TRADE_NAV_ITEM,
        // PRICING_NAV_ITEM,
    ],
};

const API_GROUP: NavGroup = {
    key: "api",
    label: "Data & API",
    items: [
        DATA_CATALOG_NAV_ITEM,
        DOCS_NAV_ITEM,
        SALES_PAGE,
        API_TOOLS_NAV_ITEM,
        MCP_TOOLS_NAV_ITEM,
    ],
};

const MARKET_INTELLIGENCE_GROUP: NavGroup = {
    key: "market_intelligence",
    label: "Market Intelligence",
    items: [
        OVERVIEW_NAV_ITEM,
        NETWORKS_NAV_ITEM,
        PROTOCOLS_NAV_ITEM,
        MANAGERS_NAV_ITEM,
        ASSET_CLASSES_NAV_ITEM,
    ],
};

const MEDIA_GROUP: NavGroup = {
    key: "media",
    label: "Media",
    items: [RESEARCH_NAV_ITEM, MEDIA_CITATIONS_NAV_ITEM],
};

// // // //
// Admin routes

const USERS_ADMIN_NAV_ITEM: NavItem = {
    key: "users-admin",
    label: "User Admin",
    href: Routes.userListing,
    icon: IconTypes.User,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [Routes.userListing, Routes.userIndividual],
};

const VISIBILITY_ADMIN_NAV_ITEM: NavItem = {
    key: "visibility-admin",
    label: "Visibility",
    href: Routes.visibility,
    icon: IconTypes.Search,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [Routes.visibility],
};

const API_KEYS_ADMIN_NAV_ITEM: NavItem = {
    key: "api-keys-admin",
    label: "API Key Admin",
    href: Routes.apiKeyListing,
    icon: IconTypes.Code,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [Routes.apiKeyListing],
};

const OAUTH_CLIENTS_ADMIN_NAV_ITEM: NavItem = {
    key: "oauth-clients-admin",
    label: "OAuth Client Admin",
    href: Routes.oauthClientListing,
    icon: IconTypes.Lock,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [Routes.oauthClientListing],
};

const ORGANIZATIONS_ADMIN_NAV_ITEM: NavItem = {
    key: "organizations-admin",
    label: "Organization Admin",
    href: Routes.organizationListing,
    icon: IconTypes.UserGroup,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [Routes.organizationListing, Routes.organizationIndividual],
};

const ASSET_CHATS_ADMIN_NAV_ITEM: NavItem = {
    key: "asset-chats-admin",
    label: "Asset Chat Admin",
    href: Routes.assetChatListing,
    icon: IconTypes.MessageDollar,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [Routes.assetChatListing, Routes.assetChatIndividual],
};

const apiAccessUserRoles: NavItem = {
    key: "admin-api-access-user-roles",
    href: Routes.apiAccessUserRoleListing,
    label: "User Roles",
    icon: IconTypes.AtomSimple,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [
        Routes.apiAccessUserRoleListing,
        Routes.apiAccessUserRoleNew,
        Routes.apiAccessUserRoleEdit,
    ],
};

const apiAccessOrgRoles: NavItem = {
    key: "admin-api-access-org-roles",
    href: Routes.apiAccessOrgRoleListing,
    label: "Org Roles",
    icon: IconTypes.AtomSimple,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [
        Routes.apiAccessOrgRoleListing,
        Routes.apiAccessOrgRoleNew,
        Routes.apiAccessOrgRoleEdit,
    ],
};

const apiAccessPolicyAssignments: NavItem = {
    key: "admin-api-access-policy-assignments",
    href: Routes.apiAccessPolicyAssignmentListing,
    label: "Policy Assignments",
    icon: IconTypes.AtomSimple,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [
        Routes.apiAccessPolicyAssignmentListing,
        Routes.apiAccessPolicyAssignmentNew,
        Routes.apiAccessPolicyAssignmentIndividual,
        Routes.apiAccessPolicyAssignmentEdit,
    ],
};

const apiAccessRoles: NavItem = {
    key: "admin-api-access-roles",
    href: Routes.apiAccessRoleListing,
    label: "Roles",
    icon: IconTypes.AtomSimple,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [
        Routes.apiAccessRoleListing,
        Routes.apiAccessRoleNew,
        Routes.apiAccessRoleIndividual,
        Routes.apiAccessRoleEdit,
    ],
};

const apiAccessPolicies: NavItem = {
    key: "admin-api-access-policies",
    href: Routes.apiAccessPolicyListing,
    label: "Policies",
    icon: IconTypes.AtomSimple,
    featureFlag: FeatureNames.enableAdminTools,
    activeRoutes: [
        Routes.apiAccessPolicyListing,
        Routes.apiAccessPolicyNew,
        Routes.apiAccessPolicyIndividual,
        Routes.apiAccessPolicyEdit,
    ],
};

const API_ACCESS_GROUP: NavItem = {
    key: "admin-api-access",
    label: "API Access",
    featureFlag: FeatureNames.enableAdminTools,
    icon: IconTypes.Lock,
    defaultExpandedState: false,
    items: [
        apiAccessUserRoles,
        apiAccessOrgRoles,
        apiAccessPolicyAssignments,
        apiAccessRoles,
        apiAccessPolicies,
    ],
};

const ADMIN_GROUP: NavGroup = {
    key: "admin",
    label: "Admin",
    expandable: true,
    defaultExpandedState: true,
    featureFlag: FeatureNames.enableAdminTools,
    items: [
        VISIBILITY_ADMIN_NAV_ITEM,
        USERS_ADMIN_NAV_ITEM,
        ORGANIZATIONS_ADMIN_NAV_ITEM,
        ASSET_CHATS_ADMIN_NAV_ITEM,
        API_KEYS_ADMIN_NAV_ITEM,
        OAUTH_CLIENTS_ADMIN_NAV_ITEM,
        API_ACCESS_GROUP,
        {
            key: "data_tools",
            label: "Data Tools",
            defaultExpandedState: false,
            icon: IconTypes.Search,
            featureFlag: FeatureNames.enableAdminTools,
            items: [
                {
                    key: "compare_assets",
                    label: "Compare Assets",
                    href: Routes.assetComparison,
                    icon: IconTypes.SquarePollHorizontal,
                    badge: BETA_BADGE,
                    featureFlag: FeatureNames.enableAdminTools,
                },
                {
                    key: "asset-search",
                    label: "Asset Search",
                    href: Routes.assets,
                    icon: IconTypes.Boxes,
                    featureFlag: FeatureNames.enableAdminTools,
                    activeRoutes: [Routes.assetIndividual],
                },
                {
                    key: "token-timeseries",
                    label: "Token Charting",
                    href: Routes.tokenTimeseries,
                    icon: IconTypes.Chart,
                    featureFlag: FeatureNames.enableAdminTools,
                },
                {
                    key: "token-Aggregates",
                    label: "Token Aggregates",
                    href: Routes.tokenAggregates,
                    icon: IconTypes.Code,
                    featureFlag: FeatureNames.enableAdminTools,
                },
                {
                    key: "asset-Aggregates",
                    label: "Asset Aggregates",
                    href: Routes.assetAggregates,
                    icon: IconTypes.Code,
                    featureFlag: FeatureNames.enableAdminTools,
                },
                {
                    key: "asset-charting",
                    label: "Asset Charting (V4)",
                    href: Routes.assetCharting,
                    icon: IconTypes.Chart,
                    featureFlag: FeatureNames.enableAdminTools,
                },
                {
                    key: "transactions",
                    label: "Transactions",
                    href: Routes.transactions,
                    featureFlag: FeatureNames.enableAdminTools,
                    icon: IconTypes.MessageDollar,
                },
            ],
        },
    ],
};

// // // //

// NOTE - NAV_GROUPS defines the groups used in both the SidebarNav and MobileNav components
// Update this to change which nav items are displayed throughout the app
export const NAV_GROUPS = [
    HOME_GROUP,
    API_GROUP,
    MARKET_INTELLIGENCE_GROUP,
    MEDIA_GROUP,
    ADMIN_GROUP,
];
